/**
 * Environment resolution — ported from the old client's ENV_CONFIGS block, typed.
 *
 * ANON KEYS LIVE IN SOURCE ON PURPOSE. A Supabase anon key is shipped to every
 * browser that loads the app; RLS is the security boundary, not key secrecy.
 * The July 2026 security audit confirmed the old client's bundle clean on this
 * basis. What must NEVER appear here: the service-role key, the Anthropic key,
 * vendor API keys (Databento/Barchart), CRON_SECRET, or Paddle secrets — those
 * live in Edge Function env vars only.
 *
 * Phase 1 of the rebuild shares the BETA project with the old client. That is
 * deliberate (REBUILD_DECISIONS.md D5): one database is what makes the
 * differential harness possible at all.
 */

export type EnvName = 'dev' | 'next' | 'beta' | 'production';

export interface EnvConfig {
  readonly supabaseUrl: string;
  readonly supabaseAnonKey: string;
  /** Short badge shown in the UI so you always know which DB you are on. */
  readonly label: string;
}

const BETA_URL = 'https://gcrxffbkfcqdvocgniip.supabase.co';
const BETA_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImdjcnhmZmJrZmNxZHZvY2duaWlwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODAzMzA1MzAsImV4cCI6MjA5NTkwNjUzMH0.m3GFATILgx3E9j2zYA3GCdHZ4N1R_logHw3rsq4eNCo';

/**
 * ── 🔴 STOP. THESE TWO CONSTANTS ARE NAMED BACKWARDS. READ D257 FIRST. ──
 *
 * `jzrkydxenmxkafcwjyyx` below is **NOT production**. It is empty, and under
 * D257 (2026-09-06, Josh) it becomes **STAGING**.
 *
 * Production is `gcrxffbkfcqdvocgniip` — the ref this file calls `BETA_URL`.
 * It holds the real book (4,650 trades, measured) and it is promoted IN PLACE
 * at cutover: no data is copied and no logger is re-pointed. The naming here is
 * a leftover from D5, whose "reserved for cutover" aside D257 amends.
 *
 * 🚨 **The trap this comment exists to stop.** The cutover card says "fill
 * `PRODUCTION_ANON_KEY`". Filling it with the key belonging to the URL directly
 * below points `app.tradecoach.pro` at an EMPTY DATABASE: every tester signs in
 * to a book with no trades, while their loggers keep posting to `gcrx…` where
 * the app is no longer reading. A working screen on the wrong database.
 *
 * ▶ **Do not flip the naming early.** While the key is a placeholder,
 * `app.tradecoach.pro` shows a stop screen — the correct failure, and the reason
 * there is no rush. Pointing `production` at `gcrx…` with a real key means the
 * day DNS is added the live database is served with no other cutover step done.
 * The flip is its own commit, at cutover, verified — PROJECT_PLAN.md step 13.
 */
const PRODUCTION_URL = 'https://jzrkydxenmxkafcwjyyx.supabase.co';
const PRODUCTION_ANON_KEY = '__PENDING__';

const ENV_CONFIGS: Record<EnvName, EnvConfig> = {
  dev: { supabaseUrl: BETA_URL, supabaseAnonKey: BETA_ANON_KEY, label: 'DEV' },
  next: { supabaseUrl: BETA_URL, supabaseAnonKey: BETA_ANON_KEY, label: 'NEXT' },
  beta: { supabaseUrl: BETA_URL, supabaseAnonKey: BETA_ANON_KEY, label: 'BETA' },
  production: {
    supabaseUrl: PRODUCTION_URL,
    supabaseAnonKey: PRODUCTION_ANON_KEY,
    label: 'PROD',
  },
};

const isPlaceholder = (v: string): boolean => !v || v.startsWith('__');

/**
 * Handed back when the hostname is not recognised. Deliberately unreachable —
 * `misconfigured` already stops the app, and this makes a downstream bug that
 * ignored the flag fail on a dead address instead of reaching a real database.
 */
const UNKNOWN_CONFIG: EnvConfig = {
  supabaseUrl: 'https://unrecognised-host.invalid',
  supabaseAnonKey: '__UNRECOGNISED_HOST__',
  label: '—',
};

/**
 * ── 🔴 AN UNRECOGNISED HOSTNAME NO LONGER FALLS BACK. D257 IS WHY. ──
 *
 * This used to `return 'beta'` for anything it did not recognise, under "a
 * wrong-but-working env beats a white screen". That was right while beta was
 * beta. Under D257 `gcrx…` — the ref `beta` points at — **is production**, so
 * the old line means *"when in doubt, write to production."*
 *
 * The realistic visitor is not a stranger: it is a bookmarked old Pages preview
 * or a local copy, running arbitrarily old code against the live book.
 *
 * ⚠ **Recognised hosts are untouched, and that is load-bearing.**
 * `dev.tradecoach-app.pages.dev` and every preview match the `.pages.dev` rules
 * below, so the dev test loop keeps working. Only genuinely unknown names refuse.
 */
export function detectEnvName(hostname: string): EnvName | null {
  const h = hostname.toLowerCase();
  if (h === 'next.tradecoach.pro') return 'next';
  if (h === 'beta.tradecoach.pro') return 'beta';
  if (h === 'app.tradecoach.pro') return 'production';
  if (h.endsWith('.tradecoach-app.pages.dev')) return 'next'; // CF preview deploys
  if (h === 'tradecoach-app.pages.dev') return 'next';
  if (h === 'localhost' || h === '127.0.0.1') return 'dev';
  // Not recognised — including `''`, which is a `file://` copy. The caller must
  // refuse to connect rather than guess. PROJECT_PLAN.md step 10.
  return null;
}

export interface ResolvedEnv {
  /** `null` when the hostname was not recognised at all. */
  readonly name: EnvName | null;
  readonly config: EnvConfig;
  /**
   * Either this host was RECOGNISED and its own environment is not filled in,
   * or the host was not recognised at all.
   *
   * ⚠ The app must refuse to run rather than read or write anything. Nothing
   * downstream may treat this as a normal boot.
   */
  readonly misconfigured: boolean;
  /** Why it refused — so the stop screen can say something useful. */
  readonly reason: 'unwired' | 'unknown-host' | null;
  /** The hostname as resolved, for the stop screen. */
  readonly hostname: string;
}

/**
 * ── 🔴 A RECOGNISED HOST WITH AN UNFILLED KEY MUST NOT BORROW BETA'S DATABASE ──
 *
 * This used to fall back to beta for ANY environment whose anon key was still a
 * placeholder, with a `console.warn`, under the rule "a wrong-but-working env
 * beats a white screen".
 *
 * ⚠ **This paragraph used to go on to say that rule was still right for an
 * UNKNOWN hostname, "and `detectEnvName` still does exactly that."** It no
 * longer does — D257 removed that fallback too, because the config it landed on
 * is production's. Corrected here rather than left standing: a comment
 * describing code that does not exist is this project's most-repeated defect,
 * and one sitting three lines from the function it lies about is the worst kind.
 *
 * It is wrong for `app.tradecoach.pro`. Production's key is `__PENDING__` until
 * cutover, so pointing that hostname here today would have quietly served the
 * SHARED BETA DATABASE to somebody who believes they are on production — reading
 * beta's data, and writing into it. A console warning nobody is looking at is
 * not a control, and "working" is the wrong goal when what it works on is the
 * wrong database.
 *
 * ▶ So a recognised environment whose own config is a placeholder is reported as
 * MISCONFIGURED and the app stops.
 *
 * ⚠ **An unknown hostname now stops too — it did not used to.** See the note on
 * `detectEnvName`: the fallback used to land on `beta`, and under D257 that ref
 * is production, so "when in doubt" had come to mean "write to production."
 */
export function resolveEnv(hostname: string): ResolvedEnv {
  const name = detectEnvName(hostname);
  if (name === null) {
    console.error(
      `[env] hostname ${JSON.stringify(hostname)} is not recognised — refusing to `
      + 'start. This build will not guess which database it belongs to.',
    );
    return {
      name: null,
      config: UNKNOWN_CONFIG,
      misconfigured: true,
      reason: 'unknown-host',
      hostname,
    };
  }
  const config = ENV_CONFIGS[name];
  if (isPlaceholder(config.supabaseAnonKey)) {
    console.error(
      `[env] ${name} has a placeholder anon key — refusing to start. `
      + 'This environment was never wired up; it must NOT fall back to another '
      + "project's database.",
    );
    return { name, config, misconfigured: true, reason: 'unwired', hostname };
  }
  return { name, config, misconfigured: false, reason: null, hostname };
}

export const ENV = resolveEnv(
  typeof window === 'undefined' ? '' : window.location.hostname,
);
